// Goal tactics for a bean on its feet (§10.2): ATTACK with its FLANK movement plan (approach from
// the inside, circle around, shove when lined up toward the void, grab near edges, stomp hands,
// punish whiffs, bait ready opponents), WANDER/POSITION, ITEM fetching, EVADE and the edge part
// of RECOVER, plus the reflexes that run before any goal (dodging a lunge or a glove, hopping a
// sweeper arm). Every tactic writes `brain.want` / `brain.press`; the motor does the rest.
import { NO_SLOT, TUNE } from '../constants';
import { BS, EK, MapId, RoundPhase } from '../ids';
import { MAPS } from '../maps/index';
import { chance, rand, randIntRange, randRange } from '../rng';
import { hasBody } from '../sim/bean';
import type { Bean, Entity, Vec2, World } from '../types';
import { Goal } from './brain';
import { gauss, grounded, type Ctx } from './context';
import { wantDir, wantPoint } from './motor';
import type { Seen } from './perception';
import { cakeAnchor, cakeTravel, gapHop } from './mapTactics';
import { BOT_TUNE } from './tuning';
import { dcos, dsin } from '../sim/dmath';

const F = BOT_TUNE.feel;
const FL = TUNE.bot.flank;

const P: Vec2 = { x: 0, y: 0 };
const G: Vec2 = { x: 0, y: 0 };

export function isEnemy(ctx: Ctx, slot: number): boolean {
  const me = ctx.self;
  if (slot === me.slot) return false;
  if (!ctx.bc.teamsOn) return true;
  const t = ctx.w.beans[slot].team;
  return t === 0 || t !== me.team;
}

/** Bean `slot` can be hit right now (as perceived): has a body, hittable state, not invulnerable. */
export function hittable(ctx: Ctx, slot: number): boolean {
  const o = ctx.seen[slot];
  const b = ctx.w.beans[slot];
  return o.body && b.invulnTicks === 0 && o.state !== BS.SPUN && o.state !== BS.RESPAWN && o.state !== BS.FROZEN && o.state !== BS.GRABBED;
}

/**
 * Push direction (§10.2 FLANK) for a target at (tx, ty) attacked from (bx, by): from the target
 * toward its nearest void (down the safety field, so around rails toward a gap), blended into the
 * plain attack direction when the target is far from any edge. Writes `out`; returns the target's
 * safety.
 */
export function pushDir(ctx: Ctx, tx: number, ty: number, bx: number, by: number, out: Vec2): number {
  const s = ctx.field.safety(tx, ty);
  ctx.field.gradient(tx, ty, G);
  let gx = -G.x;
  let gy = -G.y;
  const gl = Math.hypot(gx, gy);
  let ax = tx - bx;
  let ay = ty - by;
  const al = Math.hypot(ax, ay);
  if (al > 1e-6) {
    ax /= al;
    ay /= al;
  } else {
    ax = 1;
    ay = 0;
  }
  if (gl < F.minGradient || s >= F.pushFarSafety) {
    out.x = ax;
    out.y = ay;
    return s;
  }
  gx /= gl;
  gy /= gl;
  const k = s <= F.pushNearSafety ? 0 : (s - F.pushNearSafety) / (F.pushFarSafety - F.pushNearSafety);
  const x = gx * (1 - k) + ax * k;
  const y = gy * (1 - k) + ay * k;
  const l = Math.hypot(x, y);
  out.x = l > 1e-6 ? x / l : ax;
  out.y = l > 1e-6 ? y / l : ay;
  return s;
}

/** Moves (x, y) up the safety field until it has at least `margin` (a few steps); writes `out`. */
export function safePoint(ctx: Ctx, x: number, y: number, margin: number, out: Vec2): Vec2 {
  let px = x;
  let py = y;
  for (let i = 0; i < F.safePointSteps; i++) {
    if (ctx.field.safety(px, py) >= margin) break;
    ctx.field.gradient(px, py, G);
    const l = Math.hypot(G.x, G.y);
    if (l < 1e-6) break;
    px += (G.x / l) * F.safePointStep;
    py += (G.y / l) * F.safePointStep;
  }
  out.x = px;
  out.y = py;
  return out;
}

const SP: Vec2 = { x: 0, y: 0 };
const ARM = { t0: 0, t1: 0, dir: 1 };

/** Ticks until bean `slot` can shove again as far as the bot can tell (from its last observed lunge). */
export function shoveCooldownSeen(ctx: Ctx, slot: number): number {
  const since = ctx.w.tick - ctx.delay - ctx.per.lungeTick[slot];
  return Math.max(0, TUNE.shove.cooldownTicks - since);
}

/** Facing of a seen bean points at (x, y) within `cos` (cosine of the cone half-angle). */
function facesPoint(o: Seen, x: number, y: number, cosCone: number): boolean {
  const dx = x - o.x;
  const dy = y - o.y;
  const d = Math.hypot(dx, dy);
  if (d < 1e-6) return true;
  return (dcos(o.facing) * dx + dsin(o.facing) * dy) / d >= cosCone;
}

/**
 * A moving hazard meets a body travelling straight from (x0, y0) to (x1, y1) over `ticks`: a
 * sweeper arm reaching a point of the path before (or as) the body gets there, or a giant pinball
 * rolling through a point within its passage time of the body's visit. Sampled along the path.
 */
export function pathHazard(ctx: Ctx, x0: number, y0: number, x1: number, y1: number, ticks: number): boolean {
  const hz = ctx.hz;
  if (hz.armCount === 0 && hz.ballCount === 0) return false;
  const r = ctx.self.radius;
  for (let k = 0; k <= F.pathSamples; k++) {
    const f = k / F.pathSamples;
    const t = f * ticks;
    const x = x0 + (x1 - x0) * f;
    const y = y0 + (y1 - y0) * f;
    if (hz.armCount > 0 && hz.sweeperTicks(x, y, r) < t + F.lungeArmSlackTicks) return true;
    if (hz.ballCount > 0) {
      const tb = hz.ballTicks(x, y, r, t + F.ballSlackTicks);
      if (tb < 1e9 && tb > t - F.ballPassTicks) return true;
    }
  }
  return false;
}

/**
 * Safe to start a lunge: no arm or ball meets it, it does not carry us into a bumper, and its end
 * plus the follow-through slide stays on the floor. A lunge passes through beans (§2.5: identical
 * whether or not it hits), so a close target never shortens it. Past the rim it ends in a teeter
 * (§2.9's key rule): taken for a target within reach when nobody else could boop us while we
 * windmill, otherwise only on the per-difficulty edge greed (rolled once a second). A crown wearer
 * never risks it.
 */
export function lungeOk(ctx: Ctx, dx: number, dy: number, d: number): boolean {
  const b = ctx.self;
  const br = ctx.brain;
  const l = TUNE.shove.lungeSpeed * TUNE.shove.lungeTicks / 60 + F.lungeFollowThrough;
  const ux = dx / d;
  const uy = dy / d;
  // A lunge is locked: no arm (Spin Cycle) or rolling ball (Pinball) may reach any point of it
  // around the time we pass. Our own aim wobbles (§10.6 σ), so the paths a little to either side
  // must be clear too. Bumpers: shoving someone into one means lunging toward it, a risk people
  // take (the lunge itself must stop short of it); a crown wearer, whom any bump costs the crown,
  // keeps the follow-through and the wobble clear of them as well, with twice the room.
  const strict = b.holdsCrown;
  const spread = br.p.aimNoise * (strict ? F.lungeSpreadCrown : F.lungeSpread);
  const lunge = TUNE.shove.lungeSpeed * TUNE.shove.lungeTicks / 60;
  for (let k = -1; k <= 1; k++) {
    const c = k === 0 ? 1 : dcos(spread);
    const s = k === 0 ? 0 : k * dsin(spread);
    const rx = ux * c - uy * s;
    const ry = ux * s + uy * c;
    if (pathHazard(ctx, b.pos.x, b.pos.y, b.pos.x + rx * l, b.pos.y + ry * l, TUNE.shove.lungeTicks + F.lungeFollowTicks)) return false;
    if ((strict || k === 0) && ctx.field.launchOnSegment(b.pos.x, b.pos.y, b.pos.x + rx * (strict ? l : lunge), b.pos.y + ry * (strict ? l : lunge), b.radius, strict ? 0.15 : 0, strict)) return false;
  }
  const ex = b.pos.x + ux * l;
  const ey = b.pos.y + uy * l;
  // A crown wearer also wants a moment of peace where the lunge leaves it (no arm right behind).
  if (strict && ctx.hz.armCount > 0 && ctx.hz.sweeperTicks(ex, ey, b.radius) < TUNE.shove.lungeTicks + F.lungeFollowTicks + F.crownArmClearTicks) return false;
  if (ctx.field.safety(ex, ey) > (b.holdsCrown ? F.crownLungeEndSafety : F.lungeEndSafety)) return true;
  if (b.holdsCrown || d >= F.rimLungeRange) return false;
  // Past the rim the lunge ends in a teeter (§2.9), which a good player saves: worth it for a
  // target in reach, as long as nobody else is close enough to boop us while we windmill.
  if (!enemyNear(ctx, ex, ey, F.teeterThreatRange, br.target)) return true;
  if (ctx.w.tick - br.greedTick >= F.greedRollTicks || ctx.w.tick < br.greedTick) {
    br.greedTick = ctx.w.tick;
    br.greedy = chance(br, br.p.edgeGreed);
  }
  return br.greedy;
}

/** An enemy (other than `except`) with a body within `range` of (x, y), as perceived. */
function enemyNear(ctx: Ctx, x: number, y: number, range: number, except: number): boolean {
  for (let s = 0; s < ctx.seen.length; s++) {
    if (s === ctx.self.slot || s === except || !isEnemy(ctx, s) || !ctx.seen[s].body) continue;
    if (Math.hypot(ctx.seen[s].x - x, ctx.seen[s].y - y) < range) return true;
  }
  return false;
}

/** Requests a shove at (x, y) leading the target's velocity a little; the motor adds aim noise. */
export function shoveAt(ctx: Ctx, o: Seen, x: number, y: number): void {
  const b = ctx.self;
  const br = ctx.brain;
  const d = Math.hypot(x - b.pos.x, y - b.pos.y);
  const lead = Math.min(F.shoveLeadMaxSec, Math.max(0, (d - F.shoveLeadFrom) / TUNE.shove.lungeSpeed));
  br.press.shove = true;
  br.press.noisy = true;
  br.press.sx = x + o.vx * lead - b.pos.x;
  br.press.sy = y + o.vy * lead - b.pos.y;
  br.commitAt = -1;
}

/** Grabbable as perceived (§2.6 list, §2.14 table): not big, not carrying, not hanging, not invulnerable. */
function grabbable(ctx: Ctx, slot: number): boolean {
  const o = ctx.seen[slot];
  const t = ctx.w.beans[slot];
  if (t.shakeTicks > 0 || t.invulnTicks > 0) return false;
  switch (o.state) {
    case BS.NORMAL: case BS.LUNGE: case BS.TUMBLE: case BS.SLIP: case BS.GRAB_START: case BS.GRAB_MISS: case BS.PUNCH_WINDUP: case BS.TEETER:
      return true;
    default:
      return false;
  }
}

/** Free hit: the target cannot dodge or answer right now. */
function helpless(state: BS): boolean {
  return state === BS.TEETER || state === BS.CLIMB || state === BS.GRAB_MISS || state === BS.PUNCH || state === BS.SLIP || state === BS.YANKED;
}

// ---- ATTACK -----------------------------------------------------------------------------------

/** Walks onto a ledge-hanger's fingers (§2.9 hand stomp): slow and deliberate at the very edge. */
function stomp(ctx: Ctx, t: Bean): void {
  const br = ctx.brain;
  const wn = br.want;
  // Aim a little inside the edge point: the stomp radius reaches, and we do not teeter.
  const x = t.edgePoint.x + t.edgeNormal.x * F.stompInset;
  const y = t.edgePoint.y + t.edgeNormal.y * F.stompInset;
  const b = ctx.self;
  const d = Math.hypot(x - b.pos.x, y - b.pos.y);
  wantPoint(ctx, x, y, d > F.stompSlowDist ? 1 : F.stompSpeed);
  wn.margin = 0;
  wn.precise = true;
  wn.arrive = F.stompArrive;
  wn.contact = t.slot;
  if (d < F.stompSlowDist && br.goalSince === ctx.w.tick - 1) ctx.tele.stompAttempts++;
}

/** Rolls how long the bot hovers before it commits without an opening (personality × difficulty). */
function rollPatience(ctx: Ctx): number {
  const br = ctx.brain;
  br.rangeErr = gauss(br) * br.p.rangeErr;
  return ctx.w.tick + Math.round(randRange(br, br.p.patienceMin, br.p.patienceMax));
}

/**
 * Hot pursuit of `brain.target` with the FLANK plan (§10.2), played in human beats: close in to a
 * hover ring just outside lunge reach on the target's inner side, shuffle there while looking
 * for an opening (the target whiffed or is cooling down, is helpless, looks away, stands at the
 * edge, is dizzy, or the bot's patience ran out), then commit: dash in and shove along the push
 * direction. After the shove, back off to the standoff while the cooldown runs. Ready opponents
 * that face the bot get baited instead.
 */
export function attack(ctx: Ctx): void {
  const br = ctx.brain;
  const b = ctx.self;
  const w = ctx.w;
  const slot = br.target;
  if (slot === NO_SLOT) {
    wander(ctx);
    return;
  }
  const o = ctx.seen[slot];
  const t = w.beans[slot];
  if (!o.body || !hasBody(t)) {
    br.forceThink = true;
    wander(ctx);
    return;
  }
  const margin = ctx.margin * F.attackMarginMult;
  br.want.margin = margin;
  if (o.state === BS.LEDGE && t.state === BS.LEDGE) {
    stomp(ctx, t);
    return;
  }
  const dx = o.x - b.pos.x;
  const dy = o.y - b.pos.y;
  const d = Math.max(1e-6, Math.hypot(dx, dy));
  const ux = dx / d;
  const uy = dy / d;
  const tS = pushDir(ctx, o.x, o.y, b.pos.x, b.pos.y, P);
  const align = ux * P.x + uy * P.y;
  const ready = b.cdShove === 0 && b.state === BS.NORMAL;
  const mySafety = ctx.field.safety(b.pos.x, b.pos.y);
  const theirCd = shoveCooldownSeen(ctx, slot);
  const canHit = hittable(ctx, slot) && (o.state !== BS.FLOAT || Math.abs(o.z - b.z) <= TUNE.balloon.hitDz);
  if (br.patienceUntil < 0) {
    // A crown wearer that got away is stalked patiently; a fresh one in the scramble is not.
    const mob = w.tick - br.crownPickTick < (BOT_TUNE.modes.crownFreshTicks[w.cfg.mapId] ?? 90) && d < (BOT_TUNE.modes.crownMobRange[w.cfg.mapId] ?? 3.5);
    br.patienceUntil = rollPatience(ctx) + (t.holdsCrown && !mob ? Math.round(br.p.patienceMax * (F.crownPatienceMult - 1)) : 0);
  }

  // Openings: the reasons a person commits now rather than in a moment.
  const free = helpless(o.state);
  const punish = theirCd > F.punishMinCd && br.p.difficulty > 0 && ctx.per.whiffed(slot, w.tick, ctx.delay);
  // A runner (a fleeing crown wearer) facing away is not an opening: it is hard to line up.
  const running = Math.hypot(o.vx, o.vy) > F.runnerSpeed;
  const away = !running && !facesPoint(o, b.pos.x, b.pos.y, F.awayCos);
  const atEdge = tS < F.edgeOpening;
  const dizzy = t.dizzy >= F.dizzyOpening;
  const impatient = w.tick >= br.patienceUntil || (ctx.alive <= 2 && w.tick >= br.patienceUntil - (br.p.patienceMin >> 1));
  const opening = (free || punish || away || atEdge || dizzy || impatient) && !(t.holdsCrown && w.tick < br.crownHesitateUntil);

  if (ready && canHit) {
    // Free hits first: a teetering, climbing or whiffing bean gets booped.
    if (free && d < F.helplessRange && lungeOk(ctx, dx, dy, d)) {
      if (br.commitAt < 0) br.commitAt = w.tick + randIntRange(br, 0, br.p.commitJitter >> 1);
      if (w.tick >= br.commitAt) {
        shoveAt(ctx, o, o.x, o.y);
        br.patienceUntil = -1;
        return;
      }
    }
    // Grab near the edge (§10.2): decided once per approach, weighted by grab preference.
    if (b.cdGrab === 0 && grounded(b) && tS < FL.grabVoidDist && d < FL.grabRange && grabbable(ctx, slot)) {
      if (w.tick - br.grabRollTick > F.grabRollTicks) {
        br.grabRoll = rand(br);
        br.grabRollTick = w.tick;
      }
      if (br.grabRoll < br.p.grabPref * FL.grabChanceMult) {
        br.press.grab = true;
        br.press.sx = dx;
        br.press.sy = dy;
        br.grabRollTick = -1e9;
        br.patienceUntil = -1;
        ctx.tele.grabs++;
        return;
      }
    }
    // Shove when lined up toward the void (§10.2) and there is an opening, after a hesitation.
    const range = FL.shoveRangeBase + TUNE.shove.lungeSpeed * TUNE.shove.lungeTicks / 60 * FL.shoveLungeFrac;
    const needAlign = tS >= F.pushFarSafety ? F.interiorAlign : punish || free ? F.punishAlign : br.p.align;
    const reach = running ? range - F.runnerRangeCut : range;
    if (opening && d - br.rangeErr < reach && align > needAlign && mySafety >= ctx.margin * F.shoveSafetyFrac && lungeOk(ctx, dx, dy, d)) {
      if (br.commitAt < 0) br.commitAt = w.tick + randIntRange(br, 0, br.p.commitJitter);
      if (w.tick >= br.commitAt) {
        shoveAt(ctx, o, o.x, o.y);
        br.patienceUntil = -1;
        ctx.tele.commits[free ? 0 : punish ? 1 : away ? 2 : atEdge ? 3 : dizzy ? 4 : 5]++;
        return;
      }
    } else br.commitAt = -1;
  } else br.commitAt = -1;

  // ---- movement ----
  const wn = br.want;
  if (!ready) {
    // Cooling down: hold the FLANK standoff instead of running into the target (§10.2, WP0 note).
    const ax = o.x - P.x * F.standoffDist;
    const ay = o.y - P.y * F.standoffDist;
    if (d < FL.approachBack) wantPoint(ctx, b.pos.x - ux * F.backOffStep, b.pos.y - uy * F.backOffStep, F.backOffSpeed);
    else {
      safePoint(ctx, ax, ay, margin, SP);
      wantPoint(ctx, SP.x, SP.y, F.standoffSpeed);
    }
    wn.arrive = F.standoffArrive;
    return;
  }

  // Baiting: hover just outside a ready, facing opponent's lunge and let it commit first.
  if (br.baitUntil > w.tick && theirCd === 0 && o.state === BS.NORMAL && !away && d < F.baitMaxDist) {
    const vx = -ux;
    const vy = -uy;
    const a = br.circleSide * F.baitOrbitRad;
    const c = dcos(a);
    const s = dsin(a);
    const px = o.x + (vx * c - vy * s) * F.baitDist;
    const py = o.y + (vx * s + vy * c) * F.baitDist;
    safePoint(ctx, px, py, margin, SP);
    wantPoint(ctx, SP.x, SP.y, F.baitSpeed);
    wn.arrive = F.standoffArrive;
    return;
  }
  if (br.baitUntil < w.tick - F.baitTicks && theirCd === 0 && !away && d < F.baitMaxDist && chance(br, br.p.baitChance / 30)) {
    br.baitUntil = w.tick + F.baitTicks;
    ctx.tele.baits++;
  }

  // Approach point on the target's inner side, routed around it when on the wrong side.
  const back = opening ? FL.approachBack : F.hoverDist;
  let gx = o.x - P.x * back;
  let gy = o.y - P.y * back;
  const vx = -ux;
  const vy = -uy;
  const behind = vx * -P.x + vy * -P.y;
  if (behind < F.behindCos && d < F.routeDist + (opening ? 0 : F.hoverDist - FL.approachBack) && tS < F.pushFarSafety) {
    const cr = vx * -P.y - vy * -P.x;
    if (Math.abs(cr) > F.circleSwitch) br.circleSide = cr > 0 ? 1 : -1;
    const a = br.circleSide * F.routeStepRad;
    const c = dcos(a);
    const s = dsin(a);
    const rr = Math.max(FL.routeRadius, back);
    gx = o.x + (vx * c - vy * s) * rr;
    gy = o.y + (vx * s + vy * c) * rr;
  } else if (!opening) {
    // Hovering: shuffle side to side on the ring like a boxer.
    const a = dsin(w.tick * F.hoverRate + br.wobblePhase) * F.hoverSwing;
    const c = dcos(a);
    const s = dsin(a);
    const hx = -P.x;
    const hy = -P.y;
    gx = o.x + (hx * c - hy * s) * back;
    gy = o.y + (hx * s + hy * c) * back;
  }
  safePoint(ctx, gx, gy, margin, SP);
  const speed = opening ? (d > F.closeDist ? 1 : F.closeSpeed) : d > F.hoverDist + F.closeDist ? 1 : F.hoverSpeed;
  // Crumble Cake: the target may stand on another island; hop over to it.
  if (w.cfg.mapId === MapId.CAKE && cakeTravel(ctx, SP.x, SP.y, speed)) {
    wn.arrive = opening ? F.approachArrive : F.standoffArrive;
    return;
  }
  wantPoint(ctx, SP.x, SP.y, speed);
  wn.arrive = opening ? F.approachArrive : F.standoffArrive;
}

// ---- WANDER / POSITION ------------------------------------------------------------------------

/**
 * Picks a safe spot near the action (§10.2 WANDER) and stores it in `brain.gx/gy`. With `moveOn`
 * (the bot has stood at its spot a while) only spots a few steps away qualify: people pace.
 */
export function pickWanderPoint(ctx: Ctx, moveOn = false): void {
  const br = ctx.brain;
  const b = ctx.self;
  const map = MAPS[ctx.w.cfg.mapId];
  const margin = ctx.margin;
  let cx = ctx.cx;
  let cy = ctx.cy;
  if (br.p.centerHugger) {
    cx = (cx + map.center.x) * 0.5;
    cy = (cy + map.center.y) * 0.5;
  }
  let standoff = br.p.standoff;
  // Spin Cycle: near the hub an arm's sweep is a slow shove, out by the rim it is a launch.
  if (ctx.w.cfg.mapId === MapId.SPIN) {
    cx = map.center.x;
    cy = map.center.y;
    standoff = ctx.w.phase === RoundPhase.CALM ? F.spinWanderCalm : F.spinWanderRamp;
  }
  const base = rand(br) * Math.PI * 2;
  let best = -1e9;
  let bx = b.pos.x;
  let by = b.pos.y;
  const n = F.wanderCandidates;
  for (let i = 0; i <= n + 1; i++) {
    let x: number;
    let y: number;
    if (i < n) {
      const a = base + (i / n) * Math.PI * 2;
      x = cx + dcos(a) * standoff;
      y = cy + dsin(a) * standoff;
    } else if (i === n) {
      x = br.p.centerHugger ? map.center.x : b.pos.x;
      y = br.p.centerHugger ? map.center.y : b.pos.y;
    } else {
      x = br.gx;
      y = br.gy;
    }
    const s = ctx.field.safety(x, y);
    if (s <= 0 || ctx.field.isBlocked(x, y)) continue;
    if (moveOn && Math.hypot(x - b.pos.x, y - b.pos.y) < F.wanderMoveOnDist) continue;
    let score = Math.min(s, margin + F.wanderSafetyCap) - Math.abs(Math.hypot(x - cx, y - cy) - standoff) * F.wanderRingWeight
      - ctx.hz.danger(x, y, b.radius, true) * F.wanderDangerWeight * br.p.hazardRespect - Math.hypot(x - b.pos.x, y - b.pos.y) * F.wanderTravelWeight
      + rand(br) * F.wanderRandom;
    for (let k = 0; k < ctx.seen.length; k++) {
      if (k === b.slot || !ctx.seen[k].body) continue;
      const dd = Math.hypot(ctx.seen[k].x - x, ctx.seen[k].y - y);
      if (dd < F.wanderCrowdRadius) score -= F.wanderCrowdWeight * (1 - dd / F.wanderCrowdRadius);
    }
    if (i === n + 1) score += F.wanderKeepBonus;
    if (score > best) {
      best = score;
      bx = x;
      by = y;
    }
  }
  br.gx = bx;
  br.gy = by;
  br.pointTick = ctx.w.tick;
}

export function wander(ctx: Ctx): void {
  const br = ctx.brain;
  const b = ctx.self;
  if (cakeAnchor(ctx, SP)) {
    br.gx = SP.x;
    br.gy = SP.y;
    br.pointTick = ctx.w.tick;
    cakeTravel(ctx, SP.x, SP.y, F.wanderSpeed);
    return;
  }
  const stale = ctx.w.tick - br.pointTick > F.wanderRepickTicks;
  const arrived = Math.hypot(br.gx - b.pos.x, br.gy - b.pos.y) < F.wanderArrive && ctx.w.tick - br.pointTick > F.wanderLingerTicks;
  if (stale || arrived || ctx.field.safety(br.gx, br.gy) < ctx.margin * F.wanderMinSafetyFrac) pickWanderPoint(ctx, arrived);
  wantPoint(ctx, br.gx, br.gy, F.wanderSpeed);
}

// ---- ITEM (crate fetch) -----------------------------------------------------------------------

export function findEntity(w: World, id: number): Entity | null {
  for (const e of w.ents) if (e.id === id) return e;
  return null;
}

export function fetchCrate(ctx: Ctx): void {
  const br = ctx.brain;
  const b = ctx.self;
  const e = findEntity(ctx.w, br.targetEnt);
  if (!e || e.kind !== EK.CRATE || b.item !== 0) {
    br.forceThink = true;
    wander(ctx);
    return;
  }
  const landed = e.t >= TUNE.crate.dropTicks;
  if (landed) {
    wantPoint(ctx, e.pos.x, e.pos.y, 1);
    br.want.arrive = F.crateArrive;
    br.want.precise = true;
    return;
  }
  // Still falling: wait just outside the head-bonk radius on our side, then step in.
  const dx = b.pos.x - e.pos.x;
  const dy = b.pos.y - e.pos.y;
  const d = Math.max(1e-6, Math.hypot(dx, dy));
  const r = TUNE.crate.headBonkRadius + F.crateWaitPad;
  wantPoint(ctx, e.pos.x + (dx / d) * r, e.pos.y + (dy / d) * r, 1);
  br.want.precise = true;
}

// ---- EVADE / RECOVER --------------------------------------------------------------------------

/** Best cell within `radius` of the bot: high safety, low danger, not far (EVADE hazard, §10.2). */
export function bestSafeSpot(ctx: Ctx, radius: number, out: Vec2): Vec2 {
  const b = ctx.self;
  const f = ctx.field;
  const step = f.cell;
  let best = -1e9;
  out.x = b.pos.x;
  out.y = b.pos.y;
  const n = Math.ceil(radius / step);
  for (let j = -n; j <= n; j++) {
    for (let i = -n; i <= n; i++) {
      const x = b.pos.x + i * step;
      const y = b.pos.y + j * step;
      const d = Math.hypot(x - b.pos.x, y - b.pos.y);
      if (d > radius) continue;
      const s = f.safety(x, y);
      if (s <= F.safeSpotMinSafety) continue;
      const score = Math.min(s, ctx.margin + F.wanderSafetyCap) - ctx.hz.danger(x, y, b.radius, false) * F.safeSpotDangerWeight * ctx.brain.p.hazardRespect - d * F.safeSpotTravelWeight;
      if (score > best) {
        best = score;
        out.x = x;
        out.y = y;
      }
    }
  }
  return out;
}

export function evade(ctx: Ctx): void {
  const br = ctx.brain;
  const b = ctx.self;
  // Landed bomb: run straight away (§10.2).
  const e = br.targetEnt !== 0 ? findEntity(ctx.w, br.targetEnt) : null;
  if (e && e.kind === EK.BOMB) {
    const dx = b.pos.x - e.pos.x;
    const dy = b.pos.y - e.pos.y;
    const d = Math.max(1e-6, Math.hypot(dx, dy));
    if (d > TUNE.bomb.radius + F.bombClearPad) br.forceThink = true;
    wantDir(ctx, dx / d, dy / d, 1);
    br.want.urgent = true;
    return;
  }
  wantPoint(ctx, br.gx, br.gy, 1);
  br.want.urgent = true;
  br.want.ignoreDanger = false;
  br.want.precise = true;
}

/** Position predicted `ticks` ahead from the current velocity (and the map's slide). */
export function predictSafety(ctx: Ctx, ticks: number): number {
  const b = ctx.self;
  const t = ticks / 60;
  let x = b.pos.x + b.vel.x * t;
  let y = b.pos.y + b.vel.y * t;
  const map = MAPS[ctx.w.cfg.mapId];
  if (map.slideAccel) {
    map.slideAccel(ctx.w, b.pos.x, b.pos.y, G);
    x += 0.5 * G.x * t * t;
    y += 0.5 * G.y * t * t;
  }
  return ctx.field.safety(x, y);
}

/**
 * Edge RECOVER (§10.2): when the position 12 ticks ahead is below the margin the goal accepts,
 * snap the stick up the safety field. Returns true while recovering (hysteresis band).
 */
export function edgeRecover(ctx: Ctx, margin: number): boolean {
  const br = ctx.brain;
  const b = ctx.self;
  const s = predictSafety(ctx, TUNE.bot.recover.lookaheadTicks);
  const enter = margin - F.recoverBand;
  if (!br.recovering && s >= enter) return false;
  if (br.recovering && s >= margin + F.recoverExit && ctx.field.safety(b.pos.x, b.pos.y) >= margin) {
    br.recovering = false;
    return false;
  }
  br.recovering = true;
  ctx.field.gradient(b.pos.x + b.vel.x * F.recoverProbeSec, b.pos.y + b.vel.y * F.recoverProbeSec, G);
  let gx = G.x;
  let gy = G.y;
  if (Math.hypot(gx, gy) < 1e-6) {
    const map = MAPS[ctx.w.cfg.mapId];
    gx = map.center.x - b.pos.x;
    gy = map.center.y - b.pos.y;
  }
  wantDir(ctx, gx, gy, 1);
  br.want.urgent = true;
  br.want.margin = margin;
  gapHop(ctx, gx, gy);
  return true;
}

// ---- reflexes ---------------------------------------------------------------------------------

/**
 * A hop from here, carried by the current velocity (air control is weak), lands on the floor and
 * does not sail into a bumper on the way (bumpers stand taller than a jump).
 */
export function hopOk(ctx: Ctx): boolean {
  const b = ctx.self;
  const air = (2 * ctx.w.rules.jumpVz) / ctx.w.rules.gravity;
  const lx = b.pos.x + b.vel.x * air;
  const ly = b.pos.y + b.vel.y * air;
  if (ctx.field.safety(lx, ly) <= F.hopLandSafety || ctx.field.launchOnSegment(b.pos.x, b.pos.y, lx, ly, b.radius, 0.15)) return false;
  // Spin Cycle: not taking off under an arm, nor landing just as one arrives.
  if (ctx.hz.armCount > 0) {
    if (ctx.hz.sweeperTicks(b.pos.x, b.pos.y, b.radius) < F.armHopRise) return false;
    const t = ctx.hz.sweeperTicks(lx, ly, b.radius);
    const after = b.holdsCrown ? F.crownArmClearTicks : F.hopArmSlack;
    if (t > F.armHopRise + F.armHopAir - F.hopArmSlack && t < air * 60 + after) return false;
  }
  return true;
}

/** Answer to an incoming lunge or glove: jump, sidestep or counter-shove (§10.2 EVADE). */
function respond(ctx: Ctx, slot: number, o: Seen, windup: boolean, anticipated: boolean): void {
  const br = ctx.brain;
  const b = ctx.self;
  const dx = b.pos.x - o.x;
  const dy = b.pos.y - o.y;
  const d = Math.max(1e-6, Math.hypot(dx, dy));
  const ready = b.cdShove === 0 && b.state === BS.NORMAL;
  if (!windup && ready && chance(br, br.p.clash) && lungeOk(ctx, -dx, -dy, d)) {
    br.press.shove = true;
    br.press.noisy = true;
    br.press.sx = -dx;
    br.press.sy = -dy;
    ctx.tele.clashAttempts++;
    return;
  }
  const jumpP = anticipated ? br.p.jumpDodge * F.anticipateJumpMult : br.p.jumpDodge;
  if (grounded(b) && chance(br, jumpP) && hopOk(ctx)) {
    br.press.jump = true;
    ctx.tele.jumpDodges++;
    return;
  }
  // Sidestep perpendicular to the threat, toward the side with better footing and no hazard (an
  // arm about to sweep it, a bumper); with nowhere good to step, stand and take it.
  const px = -dy / d;
  const py = dx / d;
  const sa = sidestepScore(ctx, px + (dx / d) * F.sidestepBack, py + (dy / d) * F.sidestepBack);
  const sb = sidestepScore(ctx, -px + (dx / d) * F.sidestepBack, -py + (dy / d) * F.sidestepBack);
  if (Math.max(sa, sb) < F.sidestepMinScore) return;
  const side = sa >= sb ? 1 : -1;
  br.evx = px * side + (dx / d) * F.sidestepBack;
  br.evy = py * side + (dy / d) * F.sidestepBack;
  br.evadeUntil = ctx.w.tick + F.sidestepTicks;
  ctx.tele.sidesteps++;
  void slot;
}

/** How good a sidestep along (ux, uy) is: footing at its end, minus hazards on the way. */
function sidestepScore(ctx: Ctx, ux: number, uy: number): number {
  const b = ctx.self;
  const l = Math.max(1e-6, Math.hypot(ux, uy));
  const x = b.pos.x + (ux / l) * F.sidestepProbe;
  const y = b.pos.y + (uy / l) * F.sidestepProbe;
  let s = Math.min(ctx.field.safety(x, y), F.sidestepSafetyCap);
  if (ctx.field.launchOnSegment(b.pos.x, b.pos.y, x, y, b.radius, 0.15)) s -= F.sidestepHazardPenalty;
  s -= ctx.hz.danger(x, y, b.radius, false) * F.sidestepHazardPenalty * ctx.brain.p.hazardRespect;
  return s;
}

/**
 * Radius of the bonk a falling thing will deal where it lands, or 0 if `e` is not falling: a ghost
 * pebble (§2.12), a crate (§3.1) or a respawning sugar cube (§6.2), each behind a shadow telegraph.
 */
function dropReach(e: Entity): number {
  switch (e.kind) {
    case EK.PEBBLE: return TUNE.ghost.pebbleRadius;
    case EK.CRATE: return e.t < TUNE.crate.dropTicks ? TUNE.crate.headBonkRadius : 0;
    case EK.CUBE: return e.z > F.cubeDropZ && e.vz < 0 ? TUNE.crate.headBonkRadius : 0;
    default: return 0;
  }
}

/**
 * A shadow on the floor around us (a ghost's pebble, a crate or a cube coming down): step out from
 * under it toward the side that keeps us on our feet. Noticed after the reaction delay; whether
 * the bot notices at all is rolled once per drop (a crown wearer always looks up).
 */
function pebbleDodge(ctx: Ctx): boolean {
  const br = ctx.brain;
  const b = ctx.self;
  for (const e of ctx.w.ents) {
    const bonk = dropReach(e);
    if (bonk === 0 || e.t < ctx.delay) continue;
    const reach = bonk + b.radius + F.pebbleDodgePad;
    const dx = b.pos.x - e.pos.x;
    const dy = b.pos.y - e.pos.y;
    const d = Math.hypot(dx, dy);
    if (d > reach) continue;
    if (br.pebbleSeen !== e.id) {
      br.pebbleSeen = e.id;
      br.pebbleDodge = b.holdsCrown || chance(br, F.pebbleNotice[br.p.difficulty] ?? 0.6);
    }
    if (!br.pebbleDodge) continue;
    // Best of 8 directions: farthest from the impact after a short step, on safe ground.
    let best = -1e9;
    let bx = 0;
    let by = 0;
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      const ux = dcos(a);
      const uy = dsin(a);
      const px = b.pos.x + ux * F.pebbleStep;
      const py = b.pos.y + uy * F.pebbleStep;
      const sf = ctx.field.safety(px, py);
      const score = Math.min(Math.hypot(px - e.pos.x, py - e.pos.y), reach) * 2 + Math.min(sf, 1) * 3 - (sf < 0.15 ? 10 : 0);
      if (score > best) {
        best = score;
        bx = ux;
        by = uy;
      }
    }
    wantDir(ctx, bx, by, 1);
    br.want.urgent = true;
    ctx.tele.pebbleDodges++;
    return true;
  }
  return false;
}

/**
 * Reflexes for a bean on its feet, before the goal tactic: an active sidestep, a perceived lunge
 * or glove windup aimed at us, and hopping sweeper arms and popping bubbles. Returns true when a
 * reflex owns the stick this tick.
 */
export function reflexes(ctx: Ctx): boolean {
  const br = ctx.brain;
  const b = ctx.self;
  const w = ctx.w;
  if (br.evadeUntil > w.tick) {
    // An arm still gets hopped mid-sidestep.
    if (hazardHops(ctx)) return true;
    wantDir(ctx, br.evx, br.evy, 1);
    br.want.urgent = true;
    return true;
  }
  if (pebbleDodge(ctx)) return true;
  const ev = TUNE.bot.evade;
  for (let s = 0; s < ctx.seen.length; s++) {
    if (s === b.slot || !isEnemy(ctx, s)) continue;
    const o = ctx.seen[s];
    if (!o.body) continue;
    const lunging = o.state === BS.LUNGE;
    const windup = o.state === BS.PUNCH_WINDUP;
    if (!lunging && !windup) continue;
    const start = lunging ? ctx.per.lungeTick[s] : ctx.per.windupTick[s];
    if (br.reacted[s] === start) continue;
    const d = Math.hypot(b.pos.x - o.x, b.pos.y - o.y);
    const range = windup ? ev.threatRange + TUNE.glove.reach - TUNE.shove.reach : ev.threatRange;
    if (d > range || !facesPoint(o, b.pos.x, b.pos.y, F.threatCos)) continue;
    br.reacted[s] = start;
    respond(ctx, s, o, windup, false);
    if (br.evadeUntil > w.tick) {
      wantDir(ctx, br.evx, br.evy, 1);
      br.want.urgent = true;
      return true;
    }
    return br.press.shove;
  }
  return hazardHops(ctx);
}

/** Runs against a sweeper arm's rotation (tangent), tilted away from the slow hub and from the rim. */
function armAgainst(ctx: Ctx, dir: number): void {
  const b = ctx.self;
  ctx.brain.hazardOwned = true;
  const r = Math.max(1e-6, Math.hypot(b.pos.x, b.pos.y));
  const rx = b.pos.x / r;
  const ry = b.pos.y / r;
  const tilt = r < F.armHubRadius ? F.armRunOutward : r > F.armRimRadius ? -F.armRunOutward * 2 : 0;
  wantDir(ctx, ry * dir + rx * tilt, -rx * dir + ry * tilt, 1);
  ctx.brain.want.urgent = true;
  ctx.brain.want.ignoreDanger = true;
  ctx.brain.want.noCrowd = true;
}

/**
 * Jump sweeper arms on time (with the difficulty's timing error, rolled once per arm) and hop
 * popping bubbles. An arm that crosses too slowly to clear with one hop (near the hub, in Calm) is
 * met head on: run against its sweep so it passes under the hop sooner. Returns true when this
 * owns the stick.
 */
function hazardHops(ctx: Ctx): boolean {
  const br = ctx.brain;
  const b = ctx.self;
  const w = ctx.w;
  // Caught by an arm (or just hopped one): steer against its sweep until it has passed under.
  if (br.armEscapeUntil > w.tick && (b.state === BS.NORMAL || b.state === BS.CARRYING)) {
    armAgainst(ctx, br.armEscapeDir);
    return true;
  }
  if (!grounded(b) || b.vz > 0 || (b.state !== BS.NORMAL && b.state !== BS.CARRYING)) return false;
  if (ctx.hz.armCount > 0 && ctx.hz.sweeperWindow(b.pos.x, b.pos.y, b.radius, b.vel.x, b.vel.y, ARM)) {
    // Riding the gap between the arms (mapTactics `spinGapSpot`): running with the sweep is the
    // plan, so the reflex leaves the stick alone until an arm is really about to arrive.
    if (br.gapRideTick >= w.tick - 1 && ARM.t0 > F.gapRideReflexTicks) return false;
    if (ARM.t0 <= 0) {
      // In contact: hop now and fight the sweep in the air (the arm passes under).
      br.press.jump = true;
      br.armEscapeUntil = w.tick + F.armEscapeTicks;
      br.armEscapeDir = ARM.dir;
      ctx.tele.hazardJumps++;
      armAgainst(ctx, ARM.dir);
      return true;
    }
    if (ARM.t0 < F.armWatchTicks) {
      if (w.tick - br.hazardNoiseTick > F.armWatchTicks) {
        br.hazardNoise = gauss(br) * br.p.hazardSigma;
        br.hazardNoiseTick = w.tick;
      }
      const width = ARM.t1 - ARM.t0;
      if (width <= F.armAirTicks) {
        // Center the hop's time above the arm (ticks armHopRise..armHopRise+armHopAir after the press) on the crossing.
        // A late plan is rescued by a last-moment hop (worse bots notice later).
        const panic = F.armPanicLead[br.p.difficulty] ?? 4;
        if ((ARM.t0 + ARM.t1) / 2 + br.hazardNoise <= F.armHopRise + F.armHopAir / 2 || ARM.t0 <= panic) {
          br.press.jump = true;
          br.hazardNoiseTick = -1e9;
          br.armEscapeUntil = w.tick + F.armHopSteerTicks;
          br.armEscapeDir = ARM.dir;
          ctx.tele.hazardJumps++;
        }
        // Meet it: lean against the sweep so the crossing stays short.
        if (ARM.t0 < F.armLeanTicks) {
          armAgainst(ctx, ARM.dir);
          return true;
        }
        return false;
      }
      if (ARM.t0 < F.armRunTicks) {
        armAgainst(ctx, ARM.dir);
        return true;
      }
    }
  }
  // One hop decision per wobbling bubble (rolled once, not every tick).
  if (w.cfg.mapId === MapId.SPIN && ctx.hz.bubbleSoon(b.pos.x, b.pos.y) && w.tick - br.bubbleRollTick > F.bubbleRollTicks) {
    br.bubbleRollTick = w.tick;
    if (!chance(br, F.bubbleHopChance) || !hopOk(ctx)) return false;
    br.press.jump = true;
    ctx.tele.hazardJumps++;
  }
  return false;
}

/**
 * Anticipation at think time: an enemy charging at us with its shove ready reads like a lunge
 * about to happen; the better the bot, the more often it acts before the lunge comes.
 */
export function anticipate(ctx: Ctx): void {
  const br = ctx.brain;
  const b = ctx.self;
  if (b.state !== BS.NORMAL || br.evadeUntil > ctx.w.tick) return;
  for (let s = 0; s < ctx.seen.length; s++) {
    if (s === b.slot || !isEnemy(ctx, s)) continue;
    const o = ctx.seen[s];
    if (!o.body || o.state !== BS.NORMAL) continue;
    const dx = b.pos.x - o.x;
    const dy = b.pos.y - o.y;
    const d = Math.hypot(dx, dy);
    if (d > F.anticipateRange || d < 1e-6) continue;
    const closing = (o.vx * dx + o.vy * dy) / d - (b.vel.x * dx + b.vel.y * dy) / d;
    const minClosing = b.holdsCrown ? F.crownAnticipateClosing : F.anticipateClosing;
    if (closing < minClosing || shoveCooldownSeen(ctx, s) > 0 || !facesPoint(o, b.pos.x, b.pos.y, F.anticipateCos)) continue;
    // The crown wearer is on high alert: everyone is coming for it.
    if (!chance(br, Math.min(1, br.p.anticipate * (b.holdsCrown ? crownCaution(ctx) * F.crownAlertMult : 1)))) continue;
    ctx.tele.anticipations++;
    respond(ctx, s, o, false, true);
    return;
  }
}

/**
 * How carefully a crown wearer plays (1 = fresh): closing out a win, a person stops taking chances,
 * so alertness and the berth it keeps grow with its share of the goal (Crown Grab, §4.3).
 */
export function crownCaution(ctx: Ctx): number {
  const b = ctx.self;
  if (!b.holdsCrown) return 1;
  const share = Math.min(1, b.crownTicks / TUNE.crown.goalTicks);
  return 1 + share * F.crownCloseOut;
}

/** Nearest enemy with a body within `range` of the bot (perceived), or NO_SLOT. */
export function nearestEnemy(ctx: Ctx, range: number): number {
  const b = ctx.self;
  let best = NO_SLOT;
  let bd = range;
  for (let s = 0; s < ctx.seen.length; s++) {
    if (s === b.slot || !isEnemy(ctx, s)) continue;
    const o = ctx.seen[s];
    if (!o.body) continue;
    const d = Math.hypot(o.x - b.pos.x, o.y - b.pos.y);
    if (d < bd) {
      bd = d;
      best = s;
    }
  }
  return best;
}

/** Chaos Goblin (§10.5): shove whoever is within reach, alignment be damned. Returns true if it did. */
export function chaosShove(ctx: Ctx): boolean {
  const br = ctx.brain;
  const b = ctx.self;
  // Waiting for a crown to pop is not the moment for random shoves.
  if (br.p.shoveAnyoneRange <= 0 || b.cdShove !== 0 || b.state !== BS.NORMAL || br.goal === Goal.CROWN_FETCH) return false;
  const s = nearestEnemy(ctx, br.p.shoveAnyoneRange);
  if (s === NO_SLOT || !hittable(ctx, s)) return false;
  const o = ctx.seen[s];
  if (!lungeOk(ctx, o.x - b.pos.x, o.y - b.pos.y, Math.max(1e-6, Math.hypot(o.x - b.pos.x, o.y - b.pos.y)))) return false;
  if (br.commitAt < 0) br.commitAt = ctx.w.tick + randIntRange(br, 0, br.p.commitJitter);
  if (ctx.w.tick < br.commitAt) return false;
  br.commitAt = -1;
  // Even a goblin hesitates sometimes (one roll per opportunity).
  if (!chance(br, F.chaosShoveChance)) {
    br.commitAt = ctx.w.tick + F.chaosRethinkTicks;
    return false;
  }
  shoveAt(ctx, o, o.x, o.y);
  ctx.tele.chaosShoves++;
  return true;
}

